import type { Rng } from './rng';
import { defaultRng } from './rng';

/**
 * Generate a structurally valid UAE Tax Registration Number.
 * IBR-148-AE: 15 numeric chars, first digit '1'.
 */
export function generateUaeTrn(rng: Rng = defaultRng()): string {
  let trn = '1';
  for (let i = 0; i < 14; i++) trn += rng.int(0, 9).toString();
  return trn;
}

/** UAE mobile prefixes per Etisalat / du / Virgin Mobile UAE allocations. */
const UAE_MOBILE_PREFIXES = ['50', '52', '54', '55', '56', '58'] as const;

/** Returns the 9-digit mobile portion (e.g. "501234567"). */
export function generateUaeMobileNumber(rng: Rng = defaultRng()): string {
  const prefix = rng.pick(UAE_MOBILE_PREFIXES);
  let suffix = '';
  for (let i = 0; i < 7; i++) suffix += rng.int(0, 9).toString();
  return `${prefix}${suffix}`;
}

/** E.164 format: +971XXXXXXXXX */
export function generateUaePhone(rng: Rng = defaultRng()): string {
  return `+971${generateUaeMobileNumber(rng)}`;
}

export function generatePeppolParticipantId(trn: string): string {
  return `0088:AETRN${trn}`;
}

/**
 * Build an RFC 5322-shaped email from a person's name and a company slug.
 * Lowercases, ASCII-only.
 */
export function generateEmail(
  givenName: string,
  familyName: string,
  companySlug: string,
): string {
  const norm = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-zA-Z]/g, '')
      .toLowerCase();
  return `${norm(givenName)}.${norm(familyName)}@${companySlug}.ae`;
}

/** Invoice number with month context, e.g. INV-2026-04-0123 */
export function generateInvoiceNumber(rng: Rng = defaultRng(), now = new Date()): string {
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
  const seq = String(rng.int(1, 9999)).padStart(4, '0');
  return `INV-${yyyy}-${mm}-${seq}`;
}

/** 32-char idempotency key (hex). Doesn't need crypto-grade entropy here. */
export function generateIdempotencyKey(rng: Rng = defaultRng()): string {
  let s = '';
  for (let i = 0; i < 32; i++) s += rng.int(0, 15).toString(16);
  return s;
}
